import { useState } from "react";
import {
    Accordion,
    AccordionBody,
    AccordionHeader,
    AccordionItem,
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Row,
} from "reactstrap";

import {
    FiActivity,
    FiArrowRight,
    FiCheckCircle,
    FiChevronRight,
    FiCode,
    FiCpu,
    FiDatabase,
    FiGithub,
    FiLayers,
    FiMail,
    FiMonitor,
    FiPlayCircle,
    FiServer,
    FiSettings,
    FiShield,
    FiTerminal,
    FiZap,
} from "react-icons/fi";

import GetInTouch from "../../components/common/Contact/GetInTouch";

import "./ITSupportAgenticAIProjectPage.css";
import SEO from "../../components/common/seo/SEO";


const ITSupportAgenticAIProjectPage = () => {
    const [openFaq, setOpenFaq] = useState("");

    const toggleFaq = (id) => {
        setOpenFaq(openFaq === id ? "" : id);
    };

    const prerequisites = [
        {
            icon: <FiCode />,
            title: "Python Programming",
            description:
                "Basic understanding of Python programming and application development.",
        },
        {
            icon: <FiCpu />,
            title: "Machine Learning",
            description:
                "Familiarity with Machine Learning and Deep Learning concepts.",
        },
        {
            icon: <FiTerminal />,
            title: "Development Tools",
            description:
                "Exposure to Jupyter Notebook, TensorFlow or PyTorch.",
        },
        {
            icon: <FiSettings />,
            title: "SDLC / DevOps",
            description:
                "Knowledge of SDLC or DevOps concepts is optional but helpful.",
        },
        {
            icon: <FiServer />,
            title: "IT Service Management",
            description:
                "Interest in IT Service Management or ticketing systems such as ServiceNow or Jira.",
        },
    ];

    const technologies = [
        {
            icon: <FiCode />,
            title: "Backend",
            value: "Python",
        },
        {
            icon: <FiMonitor />,
            title: "Frontend",
            value: "TypeScript with React",
        },
        {
            icon: <FiActivity />,
            title: "Real-time Communication",
            value: "WebSocket",
        },
        {
            icon: <FiGithub />,
            title: "External Integrations",
            value: "GitHub API",
        },
        {
            icon: <FiGithub />,
            title: "Version Control",
            value: "Git",
        },
    ];

    const learningObjectives = [
        "Build a multi-agent AI system for IT support",
        "Use NLP for email intent detection",
        "Automate GitHub access/revoke via APIs",
        "Create and update IT tickets in a mock system",
        "Deploy a frontend dashboard with real-time activity logs",
        "Understand MLOps practices like CI/CD & retraining",
    ];

    const modules = [
        {
            number: "01",
            color: "blue",
            title: "Introduction and Project Setup",
            duration: "1 Hour",
            objective:
                "Understand the project scope and set up development tools.",
            topics: [
                "Overview of Agentic AI and IT automation",
                "Email-based request handling and intent recognition",
                "Use case: Automating GitHub access/revoke",
                "System Architecture",
                "Backend: Python, GitHub API, WebSockets",
                "Frontend: React + TypeScript",
                "Environment Setup",
                "Install Python, Node.js, VS Code",
                "Initialize backend/frontend folders, Git setup",
            ],
            deliverable:
                "Basic folder structure and development environment ready.",
        },
        {
            number: "02",
            color: "green",
            title: "Email Processing & Intent Detection",
            duration: "2 Hours",
            objective:
                "Build the backend that reads emails and understands intent.",
            topics: [
                "Mock email simulation in Python (JSON format)",
                "Rule-based intent detection using regex",
                "Parse details: repo_name, github_username, access_type",
                "Real-time updates with WebSocket server setup",
            ],
            deliverable:
                "Python backend that reads mock emails, detects intent, and sends events via WebSocket.",
        },
        {
            number: "03",
            color: "blue",
            title: "GitHub Integration & Ticketing",
            duration: "2 Hours",
            objective:
                "Connect to GitHub API and simulate ticket handling.",
            topics: [
                "Integrate PyGithub for granting/revoking repo access",
                "Create mock ticketing system (e.g., JSON or memory store)",
                "Handle GitHub errors (invalid user/repo)",
                "Generate detailed ticket messages",
            ],
            deliverable:
                "GitHub integration and ticket simulation complete, with real-time backend updates.",
        },
        {
            number: "04",
            color: "green",
            title: "Frontend – Activity Log with React",
            duration: "3 Hours",
            objective:
                "Build a user interface to track backend actions.",
            topics: [
                "React setup and component creation (RequestTracker)",
                "WebSocket connection for real-time updates",
                "UI enhancements using Tailwind CSS",
                "Activity log with status, repo, username, intent",
            ],
            deliverable:
                "React-based frontend with live updates from backend actions.",
        },
        {
            number: "05",
            color: "blue",
            title: "Testing and Debugging",
            duration: "1 Hour",
            objective:
                "Test the system end-to-end and ensure functionality.",
            topics: [
                "Simulate full flow: mock email → intent → GitHub action → UI log",
                "Fix bugs and add error handling",
                "Log messages in both backend and frontend",
            ],
            deliverable:
                "Fully tested system ready for demo.",
        },
        {
            number: "06",
            color: "green",
            title: "Deployment and Final Review",
            duration: "1 Hour",
            objective:
                "Deploy and wrap up the course.",
            topics: [
                "Deploy frontend and backend (local or cloud)",
                "Final project demo & Q&A session",
                "Summarize learning outcomes and future expansion ideas",
            ],
            deliverable:
                "Deployed, working project with documentation and live demo.",
        },
    ];

    const faqs = [
        {
            id: "faq-1",
            question:
                "I’ve wasted money on courses before. How is this different?",
            answer:
                "This is not just theory. You’ll build a fully working AI-powered IT support automation system — step-by-step, guided by real professionals. It’s practical, project-based, and portfolio-ready.",
        },
        {
            id: "faq-2",
            question:
                "I’m not confident in coding yet. Can I still join?",
            answer:
                "If you’ve completed some basic programming (especially through our beginner courses), you’re good to go! If not, start with our foundation courses, then return for this project.",
        },
        {
            id: "faq-3",
            question:
                "Is this project useful for job interviews or placements?",
            answer:
                "Absolutely. You’ll finish with a deployable IT automation project, integrated with GitHub and real-time logging — a great addition to your resume and GitHub portfolio.",
        },
        {
            id: "faq-4",
            question: "Is this course beginner-friendly?",
            answer:
                "It’s beginner-friendly if you’ve completed basic to intermediate programming. If not, take our intro courses first — they’re designed to prepare you for this exact project.",
        },
        {
            id: "faq-5",
            question: "What tools do I need before starting?",
            answer:
                "Just install Python, Node.js, and VS Code — we’ll guide you through setup in the first session. Everything else will be covered during the course.",
        },
        {
            id: "faq-6",
            question: "Is this really worth my time?",
            answer:
                "In just 10 focused hours, you’ll learn email automation, GitHub API, WebSocket, and React integration — tools used by real tech teams today.",
        },
        {
            id: "faq-7",
            question: "Will I receive a certificate after the course?",
            answer:
                "Yes, you will receive an industry-recognized certificate.",
        },
        {
            id: "faq-8",
            question: "Are you looking to hire candidates for companies?",
            answer:
                "Yes, we specialize in helping businesses find and hire the right talent. Our HR and recruitment services are tailored to meet your company’s specific hiring needs. Contact us to learn how we can support your hiring process.",
        },
    ];

    const relatedProjects = [
        {
            icon: <FiActivity />,
            title: "Intelligent Manufacturing Quality Control System For Tire Production",
            description:
                "Developed an AI-powered quality control system for tyre manufacturing using computer vision to monitor real-time production line videos. The system detects anomalies in the production sequence and triggers a GenAI pipeline for root cause analysis. Hosted on AWS, it enhances manufacturing efficiency and quality compliance.",
            link:
                "https://feesworth.com/ai-ml-project-intelligent-manufacturing-quality-control-system-for-tire-production/",
        },
        {
            icon: <FiDatabase />,
            title: "Rag (Retrieval Argument Generation)",
            description:
                "Optimized large language model outputs by integrating authoritative external knowledge bases, enhancing accuracy and relevance beyond pre-trained data sources.",
            link:
                "/ai-ml-project-rag-retrieval-argument-generation",
        },
        {
            icon: <FiLayers />,
            title: "Image Classification Using Naive & Deep Learning Techniques",
            description:
                "This project focuses on developing an efficient image classification system using a classical computer vision pipeline. The classification is performed using Support Vector Machines (SVM) after extracting meaningful features from input images using the Histogram of Oriented Gradients (HOG) descriptor.",
            link:
                "/ai-ml-project-image-classification-deep-learning",
        },
        {
            icon: <FiShield />,
            title: "Brain Stroke Prediction using OpenCV and CNN",
            description:
                "Developed an AI-based brain stroke prediction system using OpenCV and CNNs to analyze MRI/CT scans. The model automates stroke detection with high accuracy by extracting spatial features from medical images. Built with TensorFlow/Keras, the solution streamlines diagnosis and supports clinical decision-making.",
            link:
                "/ai-ml-project-brain-stroke-prediction-opencv-cnn",
        },
    ];

    return (

        <>
            <SEO
                title="IT Support Agentic AI Project | AI Automation | FeesWorth"
                description="Build an AI-powered IT support automation system using Python, Agentic AI, GitHub API, WebSocket, React and TypeScript. Learn email intent detection, automated ticketing, GitHub access automation and real-time activity tracking."
                keywords="IT Support Agentic AI Project, Agentic AI Project, AI IT Automation, IT Support Automation, AI Ticketing System, GitHub API Project, Python AI Project, Agentic AI Training, WebSocket React Project, IT Automation Project"
                canonical="https://feesworth.com/replace-it-help-desk-with-automated-ticketing-system-using-ai"
            />

            <div className="it-support-ai-page">

                {/* =========================================================
                HERO
            ========================================================= */}
                <section className="it-ai-hero">
                    <div className="it-ai-hero-grid" />

                    <Container>
                        <Row className="align-items-center">

                            <Col lg="7">
                                <div className="it-ai-hero-content">

                                    <Badge className="it-ai-eyebrow">
                                        AI / ML PROJECT
                                    </Badge>

                                    <h1>
                                        IT Support{" "}
                                        <span>Agentic AI</span>
                                    </h1>

                                    <p className="it-ai-hero-description">
                                        Build an AI-powered IT support automation
                                        system that processes email requests,
                                        identifies intents, creates tickets,
                                        performs GitHub actions, and displays
                                        real-time activity logs.
                                    </p>

                                    <div className="it-ai-hero-tags">
                                        <span>
                                            <FiCpu />
                                            Agentic AI
                                        </span>

                                        <span>
                                            <FiCode />
                                            Python
                                        </span>

                                        <span>
                                            <FiGithub />
                                            GitHub API
                                        </span>

                                        <span>
                                            <FiActivity />
                                            WebSocket
                                        </span>

                                        <span>
                                            <FiMonitor />
                                            React
                                        </span>
                                    </div>

                                    <div className="it-ai-hero-actions">
                                        <Button
                                            color="primary"
                                            className="it-ai-primary-btn"
                                            href="#it-ai-contact"
                                        >
                                            Let Us Call You
                                            <FiArrowRight />
                                        </Button>

                                        <Button
                                            outline
                                            className="it-ai-secondary-btn"
                                            href="#it-ai-curriculum"
                                        >
                                            <FiPlayCircle />
                                            Explore Project
                                        </Button>
                                    </div>

                                    <div className="it-ai-hero-meta">
                                        <div>
                                            <strong>10 Hours</strong>
                                            <span>Focused Training</span>
                                        </div>

                                        <div>
                                            <strong>6 Modules</strong>
                                            <span>Hands-on Learning</span>
                                        </div>

                                        <div>
                                            <strong>Live Project</strong>
                                            <span>Portfolio Ready</span>
                                        </div>
                                    </div>
                                </div>
                            </Col>

                            <Col lg="5">
                                <div className="it-ai-architecture-card">

                                    <div className="architecture-top">
                                        <div className="architecture-status">
                                            <span />
                                            AI SUPPORT SYSTEM
                                        </div>

                                        <FiZap />
                                    </div>

                                    <div className="architecture-flow">

                                        <div className="architecture-node email-node">
                                            <FiMail />
                                            <span>IT Request</span>
                                        </div>

                                        <div className="architecture-line">
                                            <FiChevronRight />
                                        </div>

                                        <div className="architecture-node ai-node">
                                            <FiCpu />
                                            <span>Intent AI</span>
                                        </div>

                                        <div className="architecture-line">
                                            <FiChevronRight />
                                        </div>

                                        <div className="architecture-node action-node">
                                            <FiGithub />
                                            <span>GitHub Action</span>
                                        </div>

                                    </div>

                                    <div className="architecture-bottom">

                                        <div>
                                            <FiServer />
                                            <span>Ticket System</span>
                                        </div>

                                        <div>
                                            <FiActivity />
                                            <span>Live Activity</span>
                                        </div>

                                        <div>
                                            <FiMonitor />
                                            <span>React Dashboard</span>
                                        </div>

                                    </div>
                                </div>
                            </Col>

                        </Row>
                    </Container>
                </section>


                {/* =========================================================
                PROJECT DESCRIPTION
            ========================================================= */}
                <section className="it-ai-section it-ai-overview">
                    <Container>

                        <div className="it-ai-section-heading">
                            <span>PROJECT OVERVIEW</span>
                            <h2>What is this project all about?</h2>
                            <p>
                                Learn how to build an automated IT ticketing
                                workflow using Agentic AI, Machine Learning,
                                Deep Learning and modern web technologies.
                            </p>
                        </div>

                        <Row className="align-items-center">

                            <Col lg="7">
                                <div className="it-ai-overview-content">

                                    <div className="it-ai-highlight">
                                        <FiZap />
                                        <div>
                                            <strong>
                                                Automation IT Ticketing System
                                            </strong>
                                            <span>
                                                Using Multi Agents, MLOps and
                                                Deep Learning Model Training
                                            </span>
                                        </div>
                                    </div>

                                    <p>
                                        Build an AI-powered IT support automation
                                        system that processes email-based IT
                                        requests, identifies intents such as
                                        GitHub access or revoke requests, creates
                                        tickets in a ticketing system such as
                                        mock ADO or ServiceNow, performs GitHub
                                        actions, and displays activity logs in
                                        a frontend.
                                    </p>

                                    <p>
                                        The project focuses on general IT requests
                                        and GitHub access/revoke functionalities.
                                        AWS is outside the scope of this project.
                                    </p>

                                </div>
                            </Col>

                            <Col lg="5">
                                <Card className="it-ai-scope-card">
                                    <CardBody>

                                        <div className="it-ai-scope-icon">
                                            <FiLayers />
                                        </div>

                                        <h3>Project Scope</h3>

                                        <div className="scope-item">
                                            <FiCheckCircle />
                                            <span>
                                                General IT support requests
                                            </span>
                                        </div>

                                        <div className="scope-item">
                                            <FiCheckCircle />
                                            <span>
                                                GitHub access / revoke
                                            </span>
                                        </div>

                                        <div className="scope-item">
                                            <FiCheckCircle />
                                            <span>
                                                Automated ticket creation
                                            </span>
                                        </div>

                                        <div className="scope-item">
                                            <FiCheckCircle />
                                            <span>
                                                Real-time activity tracking
                                            </span>
                                        </div>

                                        <div className="scope-item">
                                            <FiCheckCircle />
                                            <span>
                                                React dashboard
                                            </span>
                                        </div>

                                    </CardBody>
                                </Card>
                            </Col>

                        </Row>
                    </Container>
                </section>


                {/* =========================================================
                PREREQUISITES
            ========================================================= */}
                <section className="it-ai-section it-ai-prerequisites">
                    <Container>

                        <div className="it-ai-section-heading">
                            <span>GET READY</span>
                            <h2>What You Should Know Before Starting</h2>
                            <p>
                                A basic foundation in programming and AI concepts
                                will help you get the most from this project.
                            </p>
                        </div>

                        <Row>
                            {prerequisites.map((item, index) => (
                                <Col
                                    md="6"
                                    lg="4"
                                    key={index}
                                    className="mb-4"
                                >
                                    <Card className="it-ai-info-card">
                                        <CardBody>

                                            <div className="it-ai-card-icon">
                                                {item.icon}
                                            </div>

                                            <h3>{item.title}</h3>

                                            <p>
                                                {item.description}
                                            </p>

                                        </CardBody>
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                    </Container>
                </section>


                {/* =========================================================
                TECHNOLOGIES
            ========================================================= */}
                <section className="it-ai-section it-ai-technologies">
                    <Container>

                        <div className="it-ai-section-heading">
                            <span>TECH STACK</span>
                            <h2>Tools & Technologies</h2>
                            <p>
                                What tools and technologies will be used in
                                this project.
                            </p>
                        </div>

                        <Row>
                            {technologies.map((technology, index) => (
                                <Col
                                    sm="6"
                                    lg="4"
                                    xl="2"
                                    key={index}
                                    className="mb-4"
                                >
                                    <Card className="it-ai-tech-card">
                                        <CardBody>

                                            <div className="it-ai-tech-icon">
                                                {technology.icon}
                                            </div>

                                            <span>
                                                {technology.title}
                                            </span>

                                            <strong>
                                                {technology.value}
                                            </strong>

                                        </CardBody>
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                    </Container>
                </section>


                {/* =========================================================
                LEARNING OBJECTIVES
            ========================================================= */}
                <section className="it-ai-section it-ai-objectives">
                    <Container>

                        <Row className="align-items-center">

                            <Col lg="5">
                                <div className="it-ai-objectives-heading">

                                    <span>LEARNING OBJECTIVES</span>

                                    <h2>
                                        What you will learn in this project
                                    </h2>

                                    <p>
                                        Go beyond theory and build the core
                                        components of an AI-powered IT support
                                        automation system.
                                    </p>

                                </div>
                            </Col>

                            <Col lg="7">
                                <div className="it-ai-objectives-list">

                                    {learningObjectives.map(
                                        (objective, index) => (
                                            <div
                                                className="it-ai-objective-item"
                                                key={index}
                                            >
                                                <div>
                                                    <FiCheckCircle />
                                                </div>

                                                <span>
                                                    {objective}
                                                </span>
                                            </div>
                                        )
                                    )}

                                </div>
                            </Col>

                        </Row>

                    </Container>
                </section>


                {/* =========================================================
                LESSON PLAN
            ========================================================= */}
                <section
                    className="it-ai-section it-ai-curriculum"
                    id="it-ai-curriculum"
                >
                    <Container>

                        <div className="it-ai-section-heading">
                            <span>LESSON PLAN</span>
                            <h2>Build the Project Step by Step</h2>
                            <p>
                                A focused 10-hour project covering backend,
                                AI intent detection, GitHub automation,
                                real-time communication and frontend
                                development.
                            </p>
                        </div>

                        <div className="it-ai-timeline">

                            {modules.map((module) => (
                                <div
                                    className={`it-ai-module ${module.color}`}
                                    key={module.number}
                                >

                                    <div className="it-ai-module-number">
                                        {module.number}
                                    </div>

                                    <Card className="it-ai-module-card">
                                        <CardBody>

                                            <div className="it-ai-module-header">

                                                <div>
                                                    <span className="it-ai-module-label">
                                                        MODULE {module.number}
                                                    </span>

                                                    <h3>
                                                        {module.title}
                                                    </h3>
                                                </div>

                                                <Badge color="" className="it-ai-duration">
                                                    {module.duration}
                                                </Badge>

                                            </div>

                                            <div className="it-ai-module-objective">
                                                <strong>
                                                    Objective:
                                                </strong>

                                                <span>
                                                    {module.objective}
                                                </span>
                                            </div>

                                            <div className="it-ai-topic-grid">

                                                {module.topics.map(
                                                    (topic, index) => (
                                                        <div
                                                            className="it-ai-topic"
                                                            key={index}
                                                        >
                                                            <FiCheckCircle />
                                                            <span>
                                                                {topic}
                                                            </span>
                                                        </div>
                                                    )
                                                )}

                                            </div>

                                            <div className="it-ai-deliverable">
                                                <FiZap />

                                                <div>
                                                    <strong>
                                                        Deliverable
                                                    </strong>

                                                    <span>
                                                        {module.deliverable}
                                                    </span>
                                                </div>
                                            </div>

                                        </CardBody>
                                    </Card>

                                </div>
                            ))}

                        </div>

                    </Container>
                </section>


                {/* =========================================================
                FAQ
            ========================================================= */}
                <section className="it-ai-section it-ai-faq">
                    <Container>

                        <div className="it-ai-section-heading">
                            <span>FAQ</span>
                            <h2>Frequently Asked Questions</h2>
                            <p>
                                Everything you need to know before starting
                                the IT Support Agentic AI project.
                            </p>
                        </div>

                        <div className="it-ai-faq-wrapper">

                            <Accordion
                                open={openFaq}
                                toggle={toggleFaq}
                            >
                                {faqs.map((faq) => (
                                    <AccordionItem
                                        key={faq.id}
                                    >
                                        <AccordionHeader targetId={faq.id}>
                                            {faq.question}
                                        </AccordionHeader>

                                        <AccordionBody accordionId={faq.id}>
                                            {faq.answer}

                                            {faq.id === "faq-8" && (
                                                <p className="it-ai-faq-link">
                                                    Please visit our page:{" "}
                                                    <a
                                                        href="https://wocons.com/"
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        WoCons – Custom AI &
                                                        CAD Solutions | Bengaluru
                                                    </a>
                                                </p>
                                            )}
                                        </AccordionBody>
                                    </AccordionItem>
                                ))}
                            </Accordion>

                        </div>

                    </Container>
                </section>


                {/* =========================================================
                RELATED PROJECTS
            ========================================================= */}
                <section className="it-ai-section it-ai-related">
                    <Container>

                        <div className="it-ai-section-heading">
                            <span>EXPLORE MORE</span>
                            <h2>Explore More AI Projects</h2>
                            <p>
                                Continue exploring practical AI and machine
                                learning projects.
                            </p>
                        </div>

                        <Row>

                            {relatedProjects.map(
                                (project, index) => (
                                    <Col
                                        lg="6"
                                        key={index}
                                        className="mb-4"
                                    >
                                        <Card className="it-ai-related-card">
                                            <CardBody>

                                                <div className="it-ai-related-icon">
                                                    {project.icon}
                                                </div>

                                                <h3>
                                                    {project.title}
                                                </h3>

                                                <p>
                                                    {project.description}
                                                </p>

                                                <a
                                                    href={project.link}
                                                    className="it-ai-related-link"
                                                >
                                                    View Course Page
                                                    <FiArrowRight />
                                                </a>

                                            </CardBody>
                                        </Card>
                                    </Col>
                                )
                            )}

                        </Row>

                    </Container>
                </section>


                {/* =========================================================
                CONTACT
            ========================================================= */}
                <section
                    className="it-ai-contact"
                    id="it-ai-contact"
                >
                    <Container>

                        <div className="it-ai-contact-heading">

                            <span>READY TO BUILD?</span>

                            <h2>
                                Let's Discuss Your IT Support AI Project
                            </h2>

                            <p>
                                Have questions about the project, tools,
                                prerequisites, or training? Send us your
                                details and our team will get back to you.
                            </p>

                        </div>

                        <div className="it-ai-contact-wrapper">

                            <GetInTouch
                                courseName="IT Support Agentic AI Project"
                                title="Get in Touch"
                                subtitle="Interested in building this AI-powered IT support automation project? Send us your details and our team will contact you."
                            />

                        </div>

                    </Container>
                </section>

            </div>

        </>

    );
};

export default ITSupportAgenticAIProjectPage;